import { DeviceFilterType } from 'xm-constants/device';
import { Priority } from 'xm-constants/Priority';
import { VoicemailMessageType } from 'xm-constants/VoicemailMessageType';
import { SEVERITY_LEVELS } from 'xm-constants/incidentManagement';

export const FlowInputId = {
  RECIPIENTS: 'recipients',
  ALERT_INCIDENT_ID: 'Incident ID',
  ALERT_RECIPIENTS: 'Recipients',
  ALERT_RESPONSES: 'responseOptions',
  ALERT_TRIGGERS: 'triggers',
  ALERT_EMAIL: 'emailTemplates',
  ALERT_SMS: 'smsTemplates',
  ALERT_VOICE: 'voiceTemplates',
  PRIORITY: ':system:handling:priority',
  EXPIRATION: ':system:handling:expirationInMinutes',
  OVERRIDE_DEVICE_RESTRICTIONS: ':system:handling:overrideDeviceRestrictions',
  ESCALATION_OVERRIDE: ':system:handling:escalationOverride',
  BYPASS_PHONE_INTRO: ':system:handling:bypassPhoneIntro',
  REQUIRE_PHONE_PASSWORD: ':system:handling:requirePhonePassword',
  VOICEMAIL_OPTIONS_RETRY: ':system:handling:voiceMailOptions:retry',
  VOICEMAIL_OPTIONS_EVERY: ':system:handling:voiceMailOptions:every',
  VOICEMAIL_OPTIONS_LEAVE: ':system:handling:voiceMailOptions:leave',
  INCIDENT: 'incident',
  SENDER_DISPLAY_NAME: ':system:sender:displayName',
  SENDER_CALLER_ID: ':system:sender:callerId',
  DEVICE_FILTER: ':system:deviceFilter',
  DEVICE_FILTER_UI_TYPE: ':system:deviceFilter:uiType',
  CONFERENCE_BRIDGE: 'conferences',
};

export const CreateAlertSetupInputIdSet = new Set([
  FlowInputId.ALERT_RECIPIENTS,
  FlowInputId.ALERT_INCIDENT_ID,
  FlowInputId.ALERT_EMAIL,
  FlowInputId.ALERT_SMS,
  FlowInputId.ALERT_VOICE,
]);

export const SenderAndHandlingFormOverridesInputIdSet = new Set([
  FlowInputId.PRIORITY,
  FlowInputId.EXPIRATION,
  FlowInputId.OVERRIDE_DEVICE_RESTRICTIONS,
  FlowInputId.ESCALATION_OVERRIDE,
  FlowInputId.BYPASS_PHONE_INTRO,
  FlowInputId.REQUIRE_PHONE_PASSWORD,
  FlowInputId.VOICEMAIL_OPTIONS_RETRY,
  FlowInputId.VOICEMAIL_OPTIONS_EVERY,
  FlowInputId.VOICEMAIL_OPTIONS_LEAVE,
  FlowInputId.SENDER_DISPLAY_NAME,
  FlowInputId.SENDER_CALLER_ID,
]);

export const DeviceFilterInputIdSet = new Set([
  FlowInputId.DEVICE_FILTER,
  FlowInputId.DEVICE_FILTER_UI_TYPE,
]);

export const FormPropertyType = {
  RECIPIENTS: 'RECIPIENTS',
  HANDLING_OPTIONS: 'HANDLING_OPTIONS',
  SENDER_OVERRIDES: 'SENDER_OVERRIDES',
  DEVICE_FILTER: 'DEVICE_FILTER',
  INCIDENT_ID: 'INCIDENT_ID',
  CONFERENCE_BRIDGE: 'CONFERENCE_BRIDGE',
};

export const FormPropertyInputDefault = {
  [FlowInputId.PRIORITY]: Priority.MEDIUM,
  [FlowInputId.EXPIRATION]: '180', // 3 hours
  [FlowInputId.OVERRIDE_DEVICE_RESTRICTIONS]: 'false',
  [FlowInputId.ESCALATION_OVERRIDE]: 'false',
  [FlowInputId.BYPASS_PHONE_INTRO]: 'false',
  [FlowInputId.REQUIRE_PHONE_PASSWORD]: 'false',
  [FlowInputId.VOICEMAIL_OPTIONS_RETRY]: '0',
  [FlowInputId.VOICEMAIL_OPTIONS_EVERY]: '60',
  [FlowInputId.VOICEMAIL_OPTIONS_LEAVE]: VoicemailMessageType.CALLBACK,
  [FlowInputId.SENDER_DISPLAY_NAME]: '',
  [FlowInputId.SENDER_CALLER_ID]: '',
  [FlowInputId.DEVICE_FILTER]: '',
  [FlowInputId.CONFERENCE_BRIDGE]: JSON.stringify({
    bridgeTypeSelector: 'NONE',
  }),
};

export const FormSectionType = {
  RECIPIENTS: 'RECIPIENTS',
  HANDLING_OPTIONS: 'HANDLING_OPTIONS',
  SENDER_OVERRIDES: 'SENDER_OVERRIDES',
  DEVICE_FILTER: 'DEVICE_FILTER',
  INCIDENT: 'INCIDENT',
  CONFERENCE_BRIDGE: 'CONFERENCE_BRIDGE',
};

export const FlowInputGroup = {
  RECIPIENTS: 'RECIPIENTS',
  DEFAULT: 'DEFAULT',
  INCIDENT: 'INCIDENT',
  OTHER: 'OTHER',
};

export const FormHandlingSection = {
  sectionType: FormSectionType.HANDLING_OPTIONS,
  visible: 'false',
};

export const FormSenderOverridesSection = {
  sectionType: FormSectionType.SENDER_OVERRIDES,
  visible: 'false',
};

export const DeviceFilterSection = {
  sectionType: FormSectionType.DEVICE_FILTER,
  visible: 'false',
};

const MAP_INCIDENT_SEVERITY_STEP_UUID = '92274a02-50fe-565e-9759-e7b42b1848e6';
const EXTERNAL_SEVERITY = 'EXTERNAL_SEVERITY';
const XM_SEVERITY_MAPPINGS = 'XM_SEVERITY_MAPPINGS';
const SEVERITY_MAPPING_RULES = 'SEVERITY_MAPPING_RULES';
const MAP_EVENT_PRIORITY_STEP_UUID = '3dfb1912-ac8e-511c-bda2-bfa26468f45f';
const EXTERNAL_PRIORITY = 'EXTERNAL_PRIORITY';
const XM_PRIORITY_MAPPINGS = 'XM_PRIORITY_MAPPINGS';
const PRIORITY_MAPPING_RULES = 'PRIORITY_MAPPING_RULES';
export const specialUiSteps = {
  // List the steps whose flowBlock config page is rendered differently from the rest
  uuids: [MAP_INCIDENT_SEVERITY_STEP_UUID, MAP_EVENT_PRIORITY_STEP_UUID],
  // List the categories / sections the inputs can be grouped under
  categories: {
    [MAP_INCIDENT_SEVERITY_STEP_UUID]: [
      EXTERNAL_SEVERITY,
      XM_SEVERITY_MAPPINGS,
      SEVERITY_MAPPING_RULES,
    ],
    [MAP_EVENT_PRIORITY_STEP_UUID]: [
      EXTERNAL_PRIORITY,
      XM_PRIORITY_MAPPINGS,
      PRIORITY_MAPPING_RULES,
    ],
  },
  // translation IDs for category descriptions
  categoryDescriptionIdsMap: {
    [MAP_INCIDENT_SEVERITY_STEP_UUID]: {
      [EXTERNAL_SEVERITY]: 'EXTERNAL_SEVERITY_DESCRIPTION',
      [XM_SEVERITY_MAPPINGS]: 'XM_SEVERITY_MAPPINGS_DESCRIPTION',
    },
    [MAP_EVENT_PRIORITY_STEP_UUID]: {
      [EXTERNAL_PRIORITY]: 'EXTERNAL_PRIORITY_DESCRIPTION',
      [XM_PRIORITY_MAPPINGS]: 'XM_PRIORITY_MAPPINGS_DESCRIPTION',
    },
  },
  // Get the uuids of inputs grouped under a category
  categoryInputUuidsMap: {
    [MAP_INCIDENT_SEVERITY_STEP_UUID]: {
      [EXTERNAL_SEVERITY]: ['e8482cf1-35ae-513c-a8ac-1b6f1165cb1d'],
      [XM_SEVERITY_MAPPINGS]: [
        '806143b2-434c-53db-afe8-ea003d6b940a',
        'fc17ab5c-f4b7-5966-8450-fa977e9290c3',
        '7c075e04-c5cc-5bc6-91fa-420ffbd9b9ff',
        '0b7644ac-a1be-5715-91b6-369b79421c43',
        '4fec054a-0e20-5e30-aae9-55446c2ae576',
        'ad0aa2a0-ae28-58c8-b8c6-f287745e807e',
      ],
      [SEVERITY_MAPPING_RULES]: [
        'c6b79431-e91e-5a8c-9fd0-1094121cfa64',
        '7a70637b-54f6-5311-af70-5f37f41f2ee5',
      ],
    },
    [MAP_EVENT_PRIORITY_STEP_UUID]: {
      [EXTERNAL_PRIORITY]: ['de18abef-205c-5537-a66f-fcf40ac45134'],
      [XM_PRIORITY_MAPPINGS]: [
        'db13181c-e7c8-500a-8f9d-45376d595292',
        '38903fed-af50-53ad-9e48-6322307154d8',
        '7d42564d-e817-5e69-a7ad-6ffdeb3c315d',
        '64aa4130-e2a6-5823-b3d1-68462fbbbb71',
      ],
      [PRIORITY_MAPPING_RULES]: [
        'c1563ca7-b0e8-5095-879a-389bb86355f8',
        '06e6f151-5984-56f5-8f57-d939f26423be',
      ],
    },
  },
  // The inputs that must be rendered as a checkbox
  checkboxInputs: {
    [MAP_INCIDENT_SEVERITY_STEP_UUID]: [
      'c6b79431-e91e-5a8c-9fd0-1094121cfa64',
      '7a70637b-54f6-5311-af70-5f37f41f2ee5',
    ],
    [MAP_EVENT_PRIORITY_STEP_UUID]: [
      'c1563ca7-b0e8-5095-879a-389bb86355f8',
      '06e6f151-5984-56f5-8f57-d939f26423be',
    ],
  },
  // The inputs that must be rendered as a dropdown/select
  dropdownInputs: {
    [MAP_INCIDENT_SEVERITY_STEP_UUID]: ['806143b2-434c-53db-afe8-ea003d6b940a'],
    [MAP_EVENT_PRIORITY_STEP_UUID]: ['db13181c-e7c8-500a-8f9d-45376d595292'],
  },
  // Define the dropdown options available per input
  dropdownOptions: {
    [MAP_INCIDENT_SEVERITY_STEP_UUID]: {
      '806143b2-434c-53db-afe8-ea003d6b940a': SEVERITY_LEVELS,
    },
    [MAP_EVENT_PRIORITY_STEP_UUID]: {
      'db13181c-e7c8-500a-8f9d-45376d595292': Priority,
    },
  },
};

export const FIND_USER_STEP_UUID = 'e21df370-ccdf-5ee2-a99a-c646fe6f1b86';
export const selectOptionUiSteps = {
  uuids: [FIND_USER_STEP_UUID],
  options: {
    [FIND_USER_STEP_UUID]: {
      FIND_USER_STEP_EMAIL_OPTION: ['e74783d0-7027-5ba7-adfe-a43e0a764a74'],
      FIND_USER_STEP_TARGET_NAME_OPTION: [
        '9ff5069b-9475-5ddd-a15e-ad8fc925a2ba',
      ],
      FIND_USER_STEP_WEB_LOGIN_OPTION: ['51a73d74-d1e0-5b0b-96f6-d8a0b1c959a4'],
      FIND_USER_STEP_CUSTOM_PROPERTY_OPTION: [
        '6178cdee-d8bf-52c1-bcd6-6e5880dfbb80',
        '5d1062bb-a556-5cbf-b66f-d8253db44ce3',
      ],
    },
  },
};

export const CreateAlertInputDefaultsByName = {
  ...FormPropertyInputDefault,
  ':system:deviceFilter:uiType': DeviceFilterType.PICKER,
};

export const InputBuiltInTypes = {
  CREATE_ALERT: 'CREATE_ALERT',
};

// TODO: remove when the `/built-in-inputs` endpoint is implemented
export const EMAIL_TEMPLATE_INPUT_ID = 'c2a258c9-70f0-5ab9-87a0-f16aac6a4f7e';
export const mockBuiltinInputs = [
  {
    id: 'c2fa8405-6461-5736-9370-96e1dc04be7c',
    builtInType: InputBuiltInTypes.CREATE_ALERT,
    name: 'Recipients',
    required: false,
  },
  {
    id: 'ac4113d2-38a7-5c77-91ba-a09708a4c15a',
    builtInType: InputBuiltInTypes.CREATE_ALERT,
    name: 'Incident ID',
    required: false,
  },
  {
    id: EMAIL_TEMPLATE_INPUT_ID,
    builtInType: InputBuiltInTypes.CREATE_ALERT,
    name: 'emailTemplates',
    required: false,
  },
  {
    id: '8fccda00-5665-53aa-90a0-267d4b796a99',
    builtInType: InputBuiltInTypes.CREATE_ALERT,
    name: 'smsTemplates',
    required: false,
  },
  {
    id: '1e0aae21-0ac6-5683-a162-36fa1f8a895c',
    builtInType: InputBuiltInTypes.CREATE_ALERT,
    name: 'voiceTemplates',
    required: false,
  },
  {
    id: '3f50d6a7-0fc9-59a1-bd97-8eb19db317bd',
    builtInType: InputBuiltInTypes.CREATE_ALERT,
    name: ':system:handling:priority',
    required: false,
  },
  {
    id: '8ff01cd9-6a5f-5ec3-9c46-a40b892b890e',
    builtInType: InputBuiltInTypes.CREATE_ALERT,
    name: ':system:handling:expirationInMinutes',
    required: false,
  },
  {
    id: 'c4501a77-1058-51d5-9044-7c4f8a39938a',
    builtInType: InputBuiltInTypes.CREATE_ALERT,
    name: ':system:handling:overrideDeviceRestrictions',
    required: false,
  },
  {
    id: '60992193-3b95-5b4e-adfd-244d5e834d67',
    builtInType: InputBuiltInTypes.CREATE_ALERT,
    name: ':system:handling:escalationOverride',
    required: false,
  },
  {
    id: '0eeeaf31-fd99-5ada-aaf5-9b4ca198cce6',
    builtInType: InputBuiltInTypes.CREATE_ALERT,
    name: ':system:handling:bypassPhoneIntro',
    required: false,
  },
  {
    id: '7ec1fde7-1816-5b50-968f-c3eafa529f62',
    builtInType: InputBuiltInTypes.CREATE_ALERT,
    name: ':system:handling:requirePhonePassword',
    required: false,
  },
  {
    id: '8298a407-9069-5331-8dee-219cdb1b4868',
    builtInType: InputBuiltInTypes.CREATE_ALERT,
    name: ':system:handling:voiceMailOptions:retry',
    required: false,
  },
  {
    id: 'ceffae29-cef3-5488-b9d1-f845c558f8a4',
    builtInType: InputBuiltInTypes.CREATE_ALERT,
    name: ':system:handling:voiceMailOptions:every',
    required: false,
  },
  {
    id: '94bf035f-007b-5da6-996b-d122ff92e7b3',
    builtInType: InputBuiltInTypes.CREATE_ALERT,
    name: ':system:handling:voiceMailOptions:leave',
    required: false,
  },
  {
    id: '71a31f95-4c03-512d-accd-8cd591444534',
    builtInType: InputBuiltInTypes.CREATE_ALERT,
    name: ':system:sender:displayName',
    required: false,
  },
  {
    id: 'e7132743-b859-54b3-b02a-8462c544e181',
    builtInType: InputBuiltInTypes.CREATE_ALERT,
    name: ':system:sender:callerId',
    required: false,
  },
  {
    id: '21dbc8d9-0c69-5ff1-99b2-f66986efcb79',
    builtInType: InputBuiltInTypes.CREATE_ALERT,
    name: ':system:deviceFilter',
    required: false,
  },
  {
    id: '9b9bc3ee-7633-57e7-b13e-443db15f6e34',
    builtInType: InputBuiltInTypes.CREATE_ALERT,
    name: ':system:deviceFilter:uiType',
    required: false,
  },
  {
    id: 'd5f190c2-4549-5f07-b14d-d51d641cf1e6',
    builtInType: InputBuiltInTypes.CREATE_ALERT,
    name: 'triggers',
    required: false,
  },
  {
    id: 'ac190a1d-00b8-5aea-8543-12459dd918a7',
    builtInType: InputBuiltInTypes.CREATE_ALERT,
    name: 'responseOptions',
    required: false,
  },
  {
    id: '707a5098-300e-50d1-8e70-30f1ec2ad99d',
    builtInType: InputBuiltInTypes.CREATE_ALERT,
    name: FlowInputId.CONFERENCE_BRIDGE,
    required: false,
  },
];

export const SenderAndHandlingOverridesFormName = 'CreateXmEventFormOverrides';
export const DeviceFilterFormName = 'CreateXmEventDeviceFilter';
export const NotifyStepSetupFormName = 'NotifyStepSetup';
export const CreateEventSetupFormName = 'CreateEventSetup';
export const NotifyStepResponsesFormName = 'NotifyStepResponses';
export const ConditionPathsFormName = 'ConditionPathsForm';
